/*
 *    Copyright [2007-2025] [wisemapping]
 *
 *   Licensed under WiseMapping Public License, Version 1.0 (the "License").
 *   It is basically the Apache License, Version 2.0 (the "License") plus the
 *   "powered by wisemapping" text requirement on every single page;
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the license at
 *
 *       https://github.com/wisemapping/wisemapping-open-source/blob/main/LICENSE.md
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import FormContainer from '../layout/form-container';
import Header from '../layout/header';
import Footer from '../layout/footer';
import { Link as RouterLink, useLocation } from 'react-router';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { trackPageView, setAnalyticsUserEmail } from '../../utils/analytics';
import { Oauth2CallbackResult, ErrorInfo } from '../../classes/client';
import { useNavigate } from 'react-router';
import GlobalError from '../form/global-error';
import { buttonsStyle } from './style';
import { ClientContext } from '../../classes/provider/client-context';
import { logCriticalError } from '../../utils';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '../../contexts/ThemeContext';
import JwtTokenConfig from '../../classes/jwt-token-config';

type OAuthProvider = 'google' | 'facebook';

const OAuthCallbackPage = (): React.ReactElement => {
  const intl = useIntl();
  const client = useContext(ClientContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { initializeThemeFromSystem } = useTheme();

  const [error, setError] = useState<ErrorInfo | undefined>();
  const [callbackResult, setCallbackResult] = useState<Oauth2CallbackResult>();

  // Route through login page so Google Ads vignette fires from a page with ads already loaded.
  const navigateAfterOAuth = (url: string): void => {
    window.location.href = `/c/login?redirect=${encodeURIComponent(url)}`;
  };

  // Determine OAuth provider based on route path
  const provider: OAuthProvider = location.pathname.includes('facebook') ? 'facebook' : 'google';
  const providerName = provider === 'facebook' ? 'Facebook' : 'Google';

  useEffect(() => {
    document.title = intl.formatMessage({
      id: 'registation.success-title',
      defaultMessage: 'Registation Success | WiseMapping',
    });
    trackPageView(window.location.pathname, 'Registration:Success');
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    // Check if user cancelled the OAuth flow
    const error = searchParams.get('error');
    const errorReason = searchParams.get('error_reason');

    if (error) {
      // User cancelled or denied access - redirect to login
      if (error === 'access_denied' || errorReason === 'user_denied') {
        navigate('/c/login');
        return;
      }

      // Other OAuth errors - show error message
      const errorDescription = searchParams.get('error_description');
      setError({
        msg: errorDescription || `OAuth error: ${error}`,
      });
      return;
    }

    // Check if this is a Spring Boot OAuth2 callback (with jwtToken in URL)
    const jwtToken = searchParams.get('jwtToken');
    const email = searchParams.get('email');
    const oauthSync = searchParams.get('oauthSync');
    const syncCode = searchParams.get('syncCode');

    if (jwtToken && email) {
      // This is a Spring Boot OAuth2 callback - process directly
      // Store JWT token
      if (jwtToken) {
        JwtTokenConfig.storeToken(jwtToken);
        setAnalyticsUserEmail(email);
      }

      const result: Oauth2CallbackResult = {
        email: email,
        oauthSync: oauthSync === 'true',
        syncCode: syncCode || undefined,
      };

      if (result.oauthSync) {
        // Initialize theme from system preference if not already set
        initializeThemeFromSystem();
        // Get redirect URL from OAuth state parameter
        const stateRedirectUrl = searchParams.get('state');
        navigateAfterOAuth(
          stateRedirectUrl && stateRedirectUrl !== 'wisemapping' ? stateRedirectUrl : '/c/maps/',
        );
        return;
      }
      setCallbackResult(result);
      return;
    }

    // Legacy OAuth callback handling (old custom OAuth endpoints)
    const oauthCode = searchParams.get('code');
    if (!oauthCode) {
      setError({
        msg: `Missing OAuth code or token in callback: ${window.location.search}`,
      });
      return;
    }

    // Get redirect URL from OAuth state parameter
    const stateRedirectUrl = searchParams.get('state');

    // Call the appropriate OAuth callback based on provider
    const callbackPromise =
      provider === 'facebook'
        ? client.processFacebookCallback(oauthCode)
        : client.processGoogleCallback(oauthCode);

    callbackPromise
      .then((result) => {
        if (result.oauthSync) {
          // Initialize theme from system preference if not already set
          initializeThemeFromSystem();
          navigateAfterOAuth(
            stateRedirectUrl && stateRedirectUrl !== 'wisemapping' ? stateRedirectUrl : '/c/maps/',
          );
          return;
        }
        setCallbackResult(result);
      })
      .catch((errorInfo: ErrorInfo) => {
        setError(errorInfo);
        logCriticalError(`Unexpected error on ${provider} OAuth callback`, errorInfo);
      });
  }, []);

  const confirmAccountSynching = (): void => {
    const callback = callbackResult;
    if (!callback) {
      throw new Error(`callbackResult can not be null`);
    }

    // Get redirect URL from OAuth state parameter
    const searchParams = new URLSearchParams(window.location.search);
    const stateRedirectUrl = searchParams.get('state');

    client
      .confirmAccountSync(callback.email, callback.syncCode, provider)
      .then(() => {
        // Initialize theme from system preference if not already set
        initializeThemeFromSystem();
        navigateAfterOAuth(
          stateRedirectUrl && stateRedirectUrl !== 'wisemapping' ? stateRedirectUrl : '/c/maps/',
        );
      })
      .catch((errorInfo: ErrorInfo) => {
        setError(errorInfo);

        // Check for session expiry during pending oauth
        let probableCause: string | undefined;
        if (errorInfo.status === 401 && callback.syncCode === 'oauth_pending') {
          probableCause =
            'Session expired while waiting for OAuth confirmation. This can happen if the session cookie is lost or expired during the redirect.';
          console.warn(probableCause);
        }

        // Add detailed debug information for troubleshooting
        const debugInfo = {
          errorInfo,
          probableCause,
          context: {
            email: callback.email,
            syncCode: callback.syncCode || '(not provided)',
            stateRedirectUrl: stateRedirectUrl || '(not provided)',
            provider,
            url: window.location.href,
          },
        };
        logCriticalError(
          `Unexpected error on confirmAccountSynching. Email: ${callback.email}, Provider: ${provider}, Status: ${errorInfo.status || 'unknown'}, Message: ${errorInfo.msg || 'none'}`,
          debugInfo,
        );
      });
  };

  // if service reports that user doesnt sync accounts yet, we need to show the options
  const needConfirmLinking = !error && callbackResult?.email && !callbackResult?.oauthSync;

  // Show the standard OAuth callback page with form container
  return (
    <div>
      <Header type="none" />
      <FormContainer>
        <Typography variant="h4" component="h1">
          {needConfirmLinking ? (
            <FormattedMessage id="registration.callback.confirm.title" defaultMessage="Confirm" />
          ) : (
            <FormattedMessage
              id="registration.callback.waiting.title"
              defaultMessage="Finishing..."
            />
          )}
        </Typography>
        <Typography paragraph>
          {needConfirmLinking ? (
            <FormattedMessage
              id="registration.callback.confirm.description"
              defaultMessage="An account with the same email was previously registered. Do you want to link your {provider} account to that WiseMapping account?"
              values={{ provider: providerName }}
            />
          ) : (
            <FormattedMessage
              id="registration.callback.waiting.description"
              defaultMessage="Please wait while we validate your identity"
            />
          )}
        </Typography>

        {error && (
          <div key="error-state">
            <GlobalError error={error} />
            <Button
              color="primary"
              size="medium"
              variant="contained"
              component={RouterLink}
              to="/c/login"
              disableElevation={true}
              css={buttonsStyle}
            >
              <FormattedMessage id="registration.callback.back" defaultMessage="Back to login" />
            </Button>
          </div>
        )}

        {!needConfirmLinking && !error && <CircularProgress />}

        {needConfirmLinking && (
          <div key="confirm-sync">
            <Button
              color="secondary"
              size="medium"
              variant="contained"
              component={RouterLink}
              to="/c/login"
              disableElevation={true}
              css={buttonsStyle}
            >
              <FormattedMessage id="registration.callback.back" defaultMessage="Back to login" />
            </Button>
            <Button
              onClick={() => {
                confirmAccountSynching();
              }}
              color="primary"
              size="medium"
              variant="contained"
              disableElevation={true}
              css={buttonsStyle}
            >
              <FormattedMessage id="registration.callback.sync" defaultMessage="Sync account" />
            </Button>
          </div>
        )}
      </FormContainer>
      <Footer />
    </div>
  );
};

export default OAuthCallbackPage;
